import { CoralogixRum } from '@coralogix/browser'
import type { PostHogInterface } from 'posthog-js'

import {
  type FlagFetchTrigger,
  countFlags,
  createInstallGuard,
  onFlagFetchTrigger,
} from '@/lib/analytics/posthog-flag-shared'

export type { FlagFetchTrigger }

export interface FlagFetchEvent {
  trigger: FlagFetchTrigger
  distinctId: string | undefined
  groups: Record<string, unknown> | undefined
  flagCount: number
  fetchSeq: number
  msSinceTrigger?: number
  stack?: string
}

export const MAX_FLAG_FETCH_EVENTS = 100

export interface FlagDebugDeps {
  /** Emits a debug event. `name` is the log name; `data` is the structured payload. */
  emit: (name: string, data: FlagFetchEvent | Record<string, unknown>) => void
  /** Returns the current time in ms. Injected for deterministic tests. */
  now: () => number
  /** When true, attaches the caller stack to events (dev only). */
  isDev: boolean
}

const alreadyInstalled = createInstallGuard()

export function installFlagDebug(posthog: PostHogInterface, deps: FlagDebugDeps): void {
  if (!alreadyInstalled(posthog)) return

  let lastTrigger: { trigger: FlagFetchTrigger; timestampMs: number; stack?: string } | null = null
  let fetchSeq = 0

  onFlagFetchTrigger(posthog, (trigger) => {
    lastTrigger = {
      trigger,
      timestampMs: deps.now(),
      stack: deps.isDev ? new Error().stack : undefined,
    }
  })

  let totalFetches = 0
  let cappedSummaryEmitted = false

  posthog.onFeatureFlags((flags) => {
    fetchSeq += 1
    totalFetches += 1
    if (fetchSeq > MAX_FLAG_FETCH_EVENTS) return // suppress per-event past the cap

    const trigger = lastTrigger?.trigger ?? 'init'
    const event: FlagFetchEvent = {
      trigger,
      distinctId: posthog.get_distinct_id(),
      groups: posthog.getGroups() as Record<string, unknown>,
      flagCount: countFlags(flags),
      fetchSeq,
    }
    if (lastTrigger) event.msSinceTrigger = deps.now() - lastTrigger.timestampMs
    if (deps.isDev && lastTrigger?.stack) event.stack = lastTrigger.stack
    deps.emit('posthog_flag_fetch', event)
  })

  window.addEventListener('pagehide', () => {
    if (cappedSummaryEmitted) return
    if (totalFetches <= MAX_FLAG_FETCH_EVENTS) return
    cappedSummaryEmitted = true
    deps.emit('posthog_flag_fetch_capped', { totalFetches, cappedAt: MAX_FLAG_FETCH_EVENTS })
  })
}

export function makeDefaultEmit(isDev: boolean): FlagDebugDeps['emit'] {
  return (name, data) => {
    // eslint-disable-next-line no-console
    if (isDev) console.debug(`[ff-debug] ${name}`, data)
    // Never send the caller stack to Coralogix.
    const { stack: _stack, ...forCoralogix } = data
    CoralogixRum.info(name, forCoralogix, { feature: 'ff-debug' })
  }
}

export function installFlagDebugWithDefaults(posthog: PostHogInterface): void {
  const isDev = import.meta.env.DEV
  installFlagDebug(posthog, { emit: makeDefaultEmit(isDev), now: () => Date.now(), isDev })
}
